import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Box, Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, generateRedirectUrl } from '@domain-group/javascript-utils'

import useBrandTheme, { useBrandThemeForm } from '@hooks/useBrandTheme'
import { generateUrls as generateBrandUrls } from '@hooks/useBrand'

import EntitySelector from '@components/EntitySelector'

const createOrUpdateBrandTheme = (params) => {
  const {
    entityState, callbacks, createFn, updateFn,
  } = params
  const { closeModal, createBrandTheme, updateBrandTheme } = callbacks

  const actionType = entityState.id ? updateFn : createFn
  const actionText = entityState.id ? 'Updated' : 'Created'

  const brandThemePayload = {
    callbacks: {
      action: actionType,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Brand Theme ${actionText}`,
  }

  if (entityState.id) return updateBrandTheme(brandThemePayload)

  return createBrandTheme(brandThemePayload)
}

const CreateOrEditBrandThemeModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditBrandThemeModal', showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { brandTheme } = modalPayload

  const {
    callbacks: {
      createBrandTheme: createFn,
      updateBrandTheme: updateFn,
    },
    creating,
    updating,
  } = useBrandTheme(brandTheme)

  const {
    entityState,
    entityState: {
      brand_id,
      id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useBrandThemeForm(brandTheme)

  const saveOrCreateButtonText = id ? 'Save Brand Theme' : 'Create Brand Theme'

  const { editBrandUrl } = generateBrandUrls({ id: brand_id })

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Create'}
        titleSecondLine="Brand Theme"
      />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>
          <EntitySelector
            boxProps={{ marginBottom: 'large' }}
            callbacks={{
              selectItem: brand => setEntityState({ brand_id: brand ? brand.id : null }),
            }}
            entityKey="brands"
            errorMessage={errors.brand_id}
            goToHref={`${editBrandUrl}?redirect=${generateRedirectUrl()}`}
            label="* Brand"
            selectedItemId={brand_id}
          />

          <Form.Field
            direction="column"
            boxProps={{ marginBottom: 'large' }}
            errorMessage={errors.title}
            label="* Title"
          >
            <input name="title" value={title} type="text" {...handlers} data-validate-field-on="change" />
          </Form.Field>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateBrandTheme({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditBrandThemeModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditBrandThemeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
